<template>
  <v-dialog
    :value="!!row"
    max-width="680px"
    scrollable
    @input="close"
  >
    <v-card
      v-if="row"
      id="payment-reconciliation-case"
    >
      <v-card-title class="d-flex align-center flex-wrap">
        <span class="me-2">Разбор заказа № {{ row.orderId }}</span>
        <span
          v-if="row.deliveryNumber"
          class="text--secondary text-body-1"
        >
          · Delivery {{ row.deliveryNumber }}
        </span>
      </v-card-title>
      <v-card-subtitle class="pb-2">
        <v-chip
          x-small
          :color="categoryColor"
          text-color="white"
          class="me-1"
        >
          {{ row.categoryTitle }}
        </v-chip>
        {{ row.restaurant }} · {{ money }}
        <div
          v-if="row.action"
          class="mt-1"
        >
          {{ row.action }}
        </div>
      </v-card-subtitle>

      <v-card-text>
        <div class="d-flex align-center flex-wrap">
          <span class="text--secondary me-3">Статус</span>
          <v-btn-toggle
            v-if="canResolve"
            :value="selected"
            mandatory
            dense
            @change="changeStatus"
          >
            <v-btn
              v-for="status in statuses"
              :key="status.value"
              :value="status.value"
              :color="selected === status.value ? status.color : undefined"
              :class="{ 'white--text': selected === status.value && status.value !== 'New' }"
              :disabled="busy || !view"
              small
            >
              {{ status.text }}
            </v-btn>
          </v-btn-toggle>
          <v-chip
            v-else
            small
            label
            :color="statusColor(view ? view.status : row.caseStatus)"
            :text-color="(view ? view.status : row.caseStatus) === 'New' ? undefined : 'white'"
          >
            {{ view ? view.statusName : row.caseStatusName }}
          </v-chip>
          <v-progress-circular
            v-if="savingStatus"
            indeterminate
            size="18"
            width="2"
            class="ms-3"
          ></v-progress-circular>
        </div>
        <div class="text-caption text--secondary mt-1 mb-4">
          {{ changedText }}
        </div>

        <v-alert
          v-if="error"
          type="error"
          dense
          text
        >
          {{ error }}
        </v-alert>

        <div class="text-subtitle-2 mb-2">
          История
        </div>
        <v-progress-linear
          v-if="loading"
          indeterminate
          class="mb-4"
        ></v-progress-linear>
        <p
          v-else-if="view && !view.history.length"
          class="text--secondary"
        >
          Пока ничего не делали.
        </p>
        <v-timeline
          v-else-if="view"
          dense
          align-top
          class="pt-0"
        >
          <v-timeline-item
            v-for="entry in view.history"
            :key="entry.id"
            small
            :color="entry.status ? statusColor(entry.status) : 'primary'"
          >
            <div class="text-caption text--secondary">
              {{ entry.author || 'сотрудник' }} · {{ time(entry.createdAt) }}
            </div>
            <div
              class="reconciliation-case-text"
              :class="{ 'text--secondary font-italic': entry.status }"
            >
              {{ entry.text }}
            </div>
          </v-timeline-item>
        </v-timeline>

        <template v-if="canResolve">
          <v-textarea
            v-model="text"
            outlined
            auto-grow
            rows="2"
            :counter="maxLength"
            :error-messages="commentError"
            :disabled="!view"
            label="Комментарий"
            placeholder="Что сделали: «Вернули деньги клиенту, чек 123»"
            class="mt-4"
            @input="commentError = null"
          ></v-textarea>
          <div class="d-flex">
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              :loading="savingComment"
              :disabled="!canSend"
              @click="addComment"
            >
              Добавить комментарий
            </v-btn>
          </div>
        </template>
        <v-alert
          v-else
          text
          dense
          type="info"
          class="mt-2 mb-0"
        >
          Менять статус и писать комментарии можно с правом «Разбор расхождений сверки оплат».
        </v-alert>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="secondary"
          outlined
          @click="close"
        >
          Закрыть
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex'
import { hasPermission } from '@/helpers/index'
import {
  COMMENT_MAX_LENGTH, caseDateTime, caseErrorText, caseStatusColor, caseStatuses,
} from '@/helpers/reconciliation'

export default {
  name: 'PaymentReconciliationCase',

  props: {
    row: { type: Object, default: null },
    categoryColor: { type: String, default: 'grey' },
    cancellation: { type: Function, required: true },
  },
  data: () => ({
    statuses: caseStatuses,
    maxLength: COMMENT_MAX_LENGTH,
    view: null,
    selected: null,
    text: '',
    loading: false,
    savingStatus: false,
    savingComment: false,
    error: null,
    commentError: null,
  }),
  computed: {
    canResolve() {
      return hasPermission('Permissions.PaymentReconciliationResolve')
    },
    busy() {
      return this.loading || this.savingStatus || this.savingComment
    },
    canSend() {
      const length = this.text.trim().length

      return Boolean(this.view) && length > 0 && length <= this.maxLength && !this.busy
    },
    changedText() {
      if (!this.view) return ''
      if (!this.view.statusChangedAt) return 'Статус ещё не меняли'

      return `Статус изменён ${this.time(this.view.statusChangedAt)}, ${this.view.statusChangedBy || 'сотрудник'}`
    },
    money() {
      return new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        .format(this.row.paidAmount || 0)
    },
  },
  watch: {
    row: {
      immediate: true,
      handler(row, previous) {
        if (!row || (previous && previous.orderId === row.orderId)) return

        this.view = null
        this.selected = row.caseStatus
        this.text = ''
        this.error = null
        this.commentError = null
        this.load(row.orderId)
      },
    },
  },
  methods: {
    ...mapActions('paymentReconciliation', ['fetchCase', 'setCaseStatus', 'addCaseComment']),
    statusColor: caseStatusColor,
    time: caseDateTime,
    close() {
      this.cancellation()
    },
    async load(orderId) {
      this.loading = true

      try {
        this.show(await this.fetchCase(orderId))
      } catch (error) {
        this.error = caseErrorText(error, 'Не удалось открыть разбор')
      } finally {
        this.loading = false
      }
    },
    show(view) {
      this.view = view
      this.selected = view.status
    },
    async changeStatus(status) {
      if (!this.view || status === this.view.status) return

      this.selected = status
      this.savingStatus = true
      this.error = null

      try {
        this.show(await this.setCaseStatus({ orderId: this.row.orderId, status }))
      } catch (error) {
        this.selected = this.view.status
        this.error = caseErrorText(error, 'Не удалось сменить статус')
      } finally {
        this.savingStatus = false
      }
    },
    async addComment() {
      if (!this.canSend) return

      this.savingComment = true
      this.commentError = null

      try {
        this.show(await this.addCaseComment({ orderId: this.row.orderId, text: this.text }))
        this.text = ''
      } catch (error) {
        this.commentError = caseErrorText(error, 'Не удалось сохранить комментарий')
      } finally {
        this.savingComment = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
#payment-reconciliation-case {
  .reconciliation-case-text {
    white-space: pre-line;
    word-break: break-word;
  }
}
</style>
